import { jobLists } from "character";
import FormComponents from "components/forms/FormComponents";
import JobIcon from "components/job-icon/JobIcon";
import NoCharacter from "components/no-character/NoCharacter";
import RelicCheckbox from "components/relic-checkbox/RelicCheckbox";
import { items } from "data-files/items";
import { quests } from "data-files/quests";
import { GameData, InputType, RelicData, View } from "enums";
import manager from "managers/app";
import { ViewRelicInfo } from "managers/types/relic-types";
import { Component } from "react";
import { connect } from "react-redux";
import t from "translations/translator";
import { AppState } from "types/app";
import utils from "util/utils";
import ContentHeader from "views/page-parts/content/content-header/ContentHeader";
import DetailsSection from "views/page-parts/content/details-section/DetailsSection";
import Obtained from "views/page-parts/content/obtained/Obtained";
import OverviewTable from "views/page-parts/content/overview-table/OverviewTable";
import { Requirement } from "views/page-parts/content/relic-requirements/types";
import { ViewToolLucisSupraProps } from "views/relics/tools/lucis/supra/types";
import { TaskInfo } from "views/type";
import "./ToolLucisSupra.css";

const mapStateToProps = (state:AppState) => {
	return {
		lang: state.userInfo.options.lang,
		gameLang: state.userInfo.options.gameLang,
		userInfo: state.userInfo,
		hideCompletedSteps: state.userInfo.options.hideCompletedSteps,
		hideCompletedTasks: state.userInfo.options.hideCompletedTasks,
		showJob: state.userInfo.options.job,
	};
};

class ToolLucisSupra extends Component<ViewToolLucisSupraProps> {
	private requirements:Requirement[] = [];
	private importantNPCs:GameData.NPC[] = [];
	private relicInfo:ViewRelicInfo = {
		jobs: jobLists.relic[RelicData.Group.TOOL_LUCIS],
		type: RelicData.Type.TOOL,
		relic: RelicData.Group.TOOL_LUCIS,
		part: RelicData.Part.TOOL_SUPRA,
		steps: [RelicData.Step.SUPRA_ITEMS],
		stepPlaceholders: {},
		next: {
			part: RelicData.Part.TOOL_LUCIS,
			view: View.RELIC_TOOLS_LUCIS_LUCIS,
		},
		previous: {
			part: RelicData.Part.TOOL_MASTERCRAFT,
			view: View.RELIC_TOOLS_LUCIS_MASTERCRAFT,
		},
	};

	constructor(props:ViewToolLucisSupraProps){
		super(props);
		this.requirements = this.getRequirements();
		this.importantNPCs = this.getImportantNPCs();
	}

	render():JSX.Element{
		const character = manager.data.getActiveCharacter();
		if(character === null){ return <NoCharacter />; }

		const completeSteps = manager.content.getCompleteSteps(character.progress, this.relicInfo);

		const demimateriaInventory = manager.data.getInventoryStatus({
			item: GameData.Item.MASTERCRAFT_DEMIMATERIA,
			relic: [this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.SUPRA_ITEMS, RelicData.Task.TASK_2],
		});

		let row = "odd-row";

		return (
			<div id="tool-lucis-supra" className="relic">
				<ContentHeader requirements={this.requirements} importantNPCs={this.importantNPCs} relicInfo={this.relicInfo}>
				</ContentHeader>
				<div id="relic-checklist">
					<OverviewTable character={character} completeSteps={completeSteps} relicInfo={this.relicInfo} />
				</div>
				<div id="relic-details">
					<h1>{t.t("Details")}</h1>
					{this.props.hideCompletedSteps && completeSteps.allComplete ? t.t("All Steps Complete") : null }
					<DetailsSection title={RelicData.Step.SUPRA_ITEMS} id={RelicData.Step.SUPRA_ITEMS} stepComplete={completeSteps[RelicData.Step.SUPRA_ITEMS]}>
						<p>{t.t("Trade in the following items at %SHOP% to obtain your %RELIC% relic", { RELIC: RelicData.Part.TOOL_SUPRA, SHOP: GameData.Shop.TALAN})}</p>
						<p>{t.t("You require a %ITEM% for each job along with another crafted item", { ITEM: GameData.Item.MASTERCRAFT_DEMIMATERIA })}</p>
						<div className="table-wrapper">
							<table className="table">
								<thead><tr><th>{t.t("Obtained")}</th><th>{t.t("Item")}</th><th>{t.t("Quantity / Job")}</th><th>{t.t("Total")}</th><th>{t.t("Used")}</th><th>{t.t("Remaining")}</th><th>{t.t("Inventory")}</th><th>{t.t("To Obtain")}</th></tr></thead>
								<tbody>
									{this.props.hideCompletedTasks && demimateriaInventory.toObtain === 0
										? null
										: <tr className="odd-row">
											<td><Obtained obtained={items[GameData.Item.MASTERCRAFT_DEMIMATERIA].obtained} qtyNeeded={demimateriaInventory.total} /></td>
											<td>{t.t(items[GameData.Item.MASTERCRAFT_DEMIMATERIA].name)}</td>
											<td>{demimateriaInventory.qtyPerJob.toLocaleString()}</td>
											<td>{demimateriaInventory.total.toLocaleString()}</td>
											<td>{demimateriaInventory.used.toLocaleString()}</td>
											<td>{demimateriaInventory.remaining.toLocaleString()}</td>
											<td><FormComponents.TextInput label={{ hide: true }} input={{ type: InputType.NUMBER, value: demimateriaInventory.inventory, dataAttributes: { item: GameData.Item.MASTERCRAFT_DEMIMATERIA }, events: { onChange: manager.content.inventoryUpdated }}} /></td>
											<td>{demimateriaInventory.toObtain.toLocaleString()}</td>
										</tr>
									}
									{
										character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.SUPRA_ITEMS)
											? <tr className="all-complete"><td colSpan={8}><p>{t.t("All Complete")}</p></td></tr>
											: null
									}
								</tbody>
							</table>
						</div>
						<div className="table-wrapper">
							<table className="table">
								<thead><tr><th>{t.t("Job")}</th><th>{t.t("Task")}</th><th>{t.t("Found")}</th><th>{t.t("Quantity")}</th><th>{t.t("Inventory")}</th><th>{t.t("To Obtain")}</th><th>{t.t("Done")}</th></tr></thead>
								<tbody>
									{
										this.relicInfo.jobs.map((job) => {
											if(this.props.showJob !== null && this.props.showJob !== job){ return null; }

											const stepComplete = character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.SUPRA_ITEMS, job);
											if(this.props.hideCompletedSteps && stepComplete){ return null; }

											const taskInfo:TaskInfo[] = [
												{ task: RelicData.Task.TASK_1, complete: character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.SUPRA_ITEMS, RelicData.Task.TASK_1, job), target: manager.relics.getTaskItem(RelicData.Step.SUPRA_ITEMS, RelicData.Task.TASK_1, job) },
											];

											const tableRows = taskInfo.map((thisTask, taskIndex) => {
												if(this.props.hideCompletedTasks && thisTask.complete){ return null; }

												let thisTarget = GameData.Item.UNKNOWN;
												if(utils.guards.isEnumValue(GameData.Item, thisTask.target)){ thisTarget = thisTask.target; }

												const inventory = manager.data.getInventoryStatus({
													item: thisTarget,
													relic: [this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.SUPRA_ITEMS, thisTask.task],
													job: job,
												});

												const showJobIcon = manager.content.shouldShowJobIcon(this.props.hideCompletedTasks, taskInfo, taskIndex);
												const rowSpan = manager.content.getRowSpan(this.props.hideCompletedTasks, taskInfo, taskIndex);

												return <tr key={`${job}-${RelicData.Step.SUPRA_ITEMS}-${thisTask.task}`} className={row}>
													{showJobIcon ? <td rowSpan={rowSpan}><JobIcon job={job} /></td> : null}
													<td>{t.t(thisTarget)}</td>
													<td><Obtained obtained={items[thisTarget].obtained} qtyNeeded={inventory.total} /></td>
													<td>{inventory.qtyPerJob.toLocaleString()}</td>
													<td><FormComponents.TextInput label={{ hide: true }} input={{ type: InputType.NUMBER, value: inventory.inventory, dataAttributes: { item: thisTarget }, events: { onChange: manager.content.inventoryUpdated }}} /></td>
													<td>{inventory.toObtain.toLocaleString()}</td>
													<td><RelicCheckbox character={character} job={job} relicInfo={this.relicInfo} step={RelicData.Step.SUPRA_ITEMS} task={thisTask.task} /></td>
												</tr>;
											});

											row = row === "odd-row" ? "even-row" : "odd-row";
											return tableRows;
										})
									}
									{
										completeSteps[RelicData.Step.SUPRA_ITEMS]
											? <tr className="all-complete"><td colSpan={7}><p>{t.t("All Complete")}</p></td></tr>
											: null
									}
								</tbody>
							</table>
						</div>
					</DetailsSection>
				</div>
			</div>
		);
	}

	componentDidMount(){
		manager.view.changeComplete();
	}

	private getRequirements():Requirement[]{
		if(!this.relicInfo || this.relicInfo.previous === null){ return []; }
		return [
			{ type: "previous part", part: this.relicInfo.previous.part, partView: this.relicInfo.previous.view },
			{ type: quests[GameData.Quest.JUST_TOOLING_AROUND].type, text: GameData.Quest.JUST_TOOLING_AROUND, lodestoneID: quests[GameData.Quest.JUST_TOOLING_AROUND].lodestoneID },
		];
	}

	private getImportantNPCs():GameData.NPC[]{
		return [GameData.NPC.TALAN];
	}
}
export default connect(mapStateToProps)(ToolLucisSupra);
